import { Suspense, lazy, useEffect, type ReactNode } from 'react';
import {
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Outlet,
  useLocation,
} from 'react-router-dom';

import { AppLayout } from '@/layouts/AppLayout';
import { AppProviders } from '@/providers/AppProviders';
import { AdminGuard } from '@/components/system/AdminGuard';
import { OrgAdminGuard } from '@/components/system/OrgAdminGuard';
import { RouteErrorBoundary } from '@/components/system/RouteErrorBoundary';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { BackendStatusBanner } from '@/components/system/BackendStatusBanner';
import { RouteSeoManager } from './RouteSeoManager';

const LandingPage = lazy(() => import('@/pages/landing').then(module => ({ default: module.LandingPage })));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then(module => ({ default: module.LoginPage })));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage').then(module => ({ default: module.RegisterPage })));
const ForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPasswordPage').then(module => ({ default: module.ForgotPasswordPage }))
);
const AuthCallbackPage = lazy(() =>
  import('@/pages/auth/AuthCallbackPage').then(module => ({ default: module.AuthCallbackPage }))
);
const AuthErrorPage = lazy(() =>
  import('@/pages/auth/AuthErrorPage').then(module => ({ default: module.AuthErrorPage }))
);
const VerifyEmailPage = lazy(() =>
  import('@/pages/auth/VerifyEmail').then(module => ({ default: module.VerifyEmailPage }))
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/auth/ResetPassword').then(module => ({ default: module.ResetPasswordPage }))
);
const NotFoundPage = lazy(() =>
  import('@/pages/errors/NotFound').then(module => ({ default: module.NotFoundPage }))
);
const ServerErrorPage = lazy(() =>
  import('@/pages/errors/ServerError').then(module => ({ default: module.ServerErrorPage }))
);
const PlatformPage = lazy(() => import('@/pages/marketing/PlatformPage').then(module => ({ default: module.PlatformPage })));
const AutomationsPage = lazy(() => import('@/pages/marketing/AutomationsPage').then(module => ({ default: module.AutomationsPage })));
const SolutionsIntegrationsPage = lazy(() =>
  import('@/pages/marketing/IntegrationsPage').then(module => ({ default: module.SolutionsIntegrationsPage }))
);
const AboutPage = lazy(() => import('@/pages/marketing/AboutPage').then(module => ({ default: module.AboutPage })));
const SecurityPage = lazy(() => import('@/pages/marketing/SecurityPage').then(module => ({ default: module.SecurityPage })));
const CareersPage = lazy(() => import('@/pages/marketing/CareersPage').then(module => ({ default: module.CareersPage })));
const CaseStudiesPage = lazy(() => import('@/pages/marketing/CaseStudiesPage').then(module => ({ default: module.CaseStudiesPage })));
const BlogPage = lazy(() => import('@/pages/marketing/BlogPage').then(module => ({ default: module.BlogPage })));
const SupportPage = lazy(() => import('@/pages/marketing/SupportPage').then(module => ({ default: module.SupportPage })));
const HowToUsePage = lazy(() => import('@/pages/marketing/HowToUsePage').then(module => ({ default: module.HowToUsePage })));
const PrivacyPage = lazy(() => import('@/pages/marketing/PrivacyPage').then(module => ({ default: module.PrivacyPage })));
const TermsPage = lazy(() => import('@/pages/marketing/TermsPage').then(module => ({ default: module.TermsPage })));
const CompliancePage = lazy(() => import('@/pages/marketing/CompliancePage').then(module => ({ default: module.CompliancePage })));
const PricingPage = lazy(() => import('@/pages/marketing/PricingPage').then(module => ({ default: module.PricingPage })));
const DashboardPage = lazy(() => import('@/pages/app/DashboardPage').then(module => ({ default: module.DashboardPage })));
const AnalyticsPage = lazy(() => import('@/pages/app/AnalyticsPage').then(module => ({ default: module.AnalyticsPage })));
const CampaignsPage = lazy(() => import('@/pages/app/CampaignsPage').then(module => ({ default: module.CampaignsPage })));
const JourneysPage = lazy(() => import('@/pages/app/JourneysPage').then(module => ({ default: module.JourneysPage })));
const AutomationPage = lazy(() => import('@/pages/app/AutomationPage'));
const PersonalizationPage = lazy(() => import('@/pages/app/PersonalizationPage').then(module => ({ default: module.PersonalizationPage })));
const ContentModerationPage = lazy(() => import('@/pages/app/ContentModerationPage').then(module => ({ default: module.ContentModerationPage })));
const ApiKeysPage = lazy(() => import('@/pages/app/ApiKeysPage').then(module => ({ default: module.ApiKeysPage })));
const TeamPage = lazy(() => import('@/pages/app/TeamPage').then(module => ({ default: module.TeamPage })));
const OrganizationPage = lazy(() => import('@/pages/app/OrganizationPage').then(module => ({ default: module.OrganizationPage })));
const BillingPage = lazy(() => import('@/pages/app/BillingPage').then(module => ({ default: module.BillingPage })));
const IntegrationsPage = lazy(() => import('@/pages/app/IntegrationsPage').then(module => ({ default: module.IntegrationsPage })));
const TrackingPage = lazy(() => import('@/pages/app/TrackingPage').then(module => ({ default: module.TrackingPage })));
const WhiteLabelPage = lazy(() => import('@/pages/app/WhiteLabelPage').then(module => ({ default: module.WhiteLabelPage })));
const AudiencesPage = lazy(() => import('@/pages/app/AudiencesPage').then(module => ({ default: module.AudiencesPage })));
const AdSpendAnalyticsPage = lazy(() => import('@/pages/app/AdSpendAnalyticsPage').then(module => ({ default: module.AdSpendAnalyticsPage })));
const AutomatedBillingPage = lazy(() => import('@/pages/app/AutomatedBillingPage').then(module => ({ default: module.AutomatedBillingPage })));
const AdminDashboardPage = lazy(() =>
  import('@/pages/app/admin/AdminDashboardPage').then(module => ({ default: module.AdminDashboardPage }))
);
const ErrorMonitoringPage = lazy(() => import('@/pages/app/admin/ErrorMonitoringPage'));
const SecurityMonitoringPage = lazy(() => import('@/pages/app/admin/SecurityMonitoringPage'));
const FounderMetricsPage = lazy(() =>
  import('@/pages/app/admin/FounderMetricsPage').then(module => ({ default: module.FounderMetricsPage }))
);
const SettingsPage = lazy(() => import('@/pages/app/settings/SettingsPage').then(module => ({ default: module.SettingsPage })));
const NotificationsPage = lazy(() => import('@/pages/app/NotificationsPage').then(module => ({ default: module.NotificationsPage })));
const EmailMarketingPage = lazy(() => import('@/pages/app/EmailMarketingPage').then(module => ({ default: module.EmailMarketingPage })));
const CRMPage = lazy(() => import('@/pages/app/CRMPage').then(module => ({ default: module.CRMPage })));
const ClientsPage = lazy(() => import('@/pages/app/ClientsPage').then(module => ({ default: module.ClientsPage })));
const MigrationCenterPage = lazy(() => import('@/pages/app/MigrationCenterPage').then(module => ({ default: module.MigrationCenterPage })));
const CreativePage = lazy(() => import('@/pages/app/CreativePage').then(module => ({ default: module.CreativePage })));
const AIAssistantPage = lazy(() => import('@/pages/app/AIAssistantPage').then(module => ({ default: module.AIAssistantPage })));
const AppSupportPage = lazy(() => import('@/pages/app/SupportPage').then(module => ({ default: module.SupportPage })));
const OnboardingFlow = lazy(() => import('@/pages/onboarding/OnboardingFlow').then(module => ({ default: module.OnboardingFlow })));
const SubscriptionPage = lazy(() => import('@/pages/app/settings/SubscriptionPage'));

const suspense = (node: ReactNode) => (
  <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-white/60">Loading…</div>}>
    {node}
  </Suspense>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootRoute />} errorElement={<RouteErrorBoundary />}>
      <Route index element={suspense(<LandingPage />)} />
      <Route path="platform" element={suspense(<PlatformPage />)} />
      <Route path="solutions">
        <Route path="automations" element={suspense(<AutomationsPage />)} />
        <Route path="integrations" element={suspense(<SolutionsIntegrationsPage />)} />
      </Route>
      <Route path="company">
        <Route path="about" element={suspense(<AboutPage />)} />
        <Route path="security" element={suspense(<SecurityPage />)} />
        <Route path="careers" element={suspense(<CareersPage />)} />
      </Route>
      <Route path="pricing" element={suspense(<PricingPage />)} />
      <Route path="resources">
        <Route path="how-to-use" element={suspense(<HowToUsePage />)} />
        <Route path="case-studies" element={suspense(<CaseStudiesPage />)} />
        <Route path="blog" element={suspense(<BlogPage />)} />
        <Route path="support" element={suspense(<SupportPage />)} />
      </Route>
      <Route path="legal">
        <Route path="privacy" element={suspense(<PrivacyPage />)} />
        <Route path="terms" element={suspense(<TermsPage />)} />
        <Route path="compliance" element={suspense(<CompliancePage />)} />
      </Route>
      {/* Top-level fallbacks for legal + support pages */}
      <Route path="privacy" element={suspense(<PrivacyPage />)} />
      <Route path="terms" element={suspense(<TermsPage />)} />
      <Route path="compliance" element={suspense(<CompliancePage />)} />
      <Route path="support" element={suspense(<SupportPage />)} />
      <Route path="onboarding" element={suspense(<OnboardingFlow />)} />
      <Route path="auth">
        <Route path="login" element={suspense(<LoginPage />)} />
        <Route path="register" element={suspense(<RegisterPage />)} />
        <Route path="forgot-password" element={suspense(<ForgotPasswordPage />)} />
        <Route path="verify-email" element={suspense(<VerifyEmailPage />)} />
        <Route path="reset-password" element={suspense(<ResetPasswordPage />)} />
        <Route path="callback" element={suspense(<AuthCallbackPage />)} />
        <Route path="error" element={suspense(<AuthErrorPage />)} />
      </Route>
      <Route element={<ProtectedRoute />}>
        <Route path="app" element={<AppLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={suspense(<DashboardPage />)} />
          <Route path="analytics" element={suspense(<AnalyticsPage />)} />
          <Route path="campaigns" element={suspense(<CampaignsPage />)} />
          <Route path="journeys" element={suspense(<JourneysPage />)} />
          <Route path="automation" element={suspense(<AutomationPage />)} />
          <Route path="personalization" element={suspense(<PersonalizationPage />)} />
          <Route path="moderation" element={suspense(<ContentModerationPage />)} />
          <Route path="billing" element={suspense(<BillingPage />)} />
          <Route path="integrations" element={suspense(<IntegrationsPage />)} />
          <Route path="tracking" element={suspense(<TrackingPage />)} />
          <Route path="audiences" element={suspense(<AudiencesPage />)} />
          <Route path="ad-spend" element={suspense(<AdSpendAnalyticsPage />)} />
          <Route path="notifications" element={suspense(<NotificationsPage />)} />
          <Route path="email-marketing" element={suspense(<EmailMarketingPage />)} />
          <Route path="crm" element={suspense(<CRMPage />)} />
          <Route path="clients" element={suspense(<ClientsPage />)} />
          <Route path="migration" element={suspense(<MigrationCenterPage />)} />
          <Route path="creative" element={suspense(<CreativePage />)} />
          <Route path="ai-assistant" element={suspense(<AIAssistantPage />)} />
          <Route path="support" element={suspense(<AppSupportPage />)} />
          {/* Org OWNER/ADMIN only — any organization's own admin, not the founder */}
          <Route element={<OrgAdminGuard />}>
            <Route path="api-keys" element={suspense(<ApiKeysPage />)} />
            <Route path="team" element={suspense(<TeamPage />)} />
            <Route path="organization" element={suspense(<OrganizationPage />)} />
          </Route>
          {/* Platform-admin only — the founder, via PLATFORM_ADMIN_EMAILS */}
          <Route element={<AdminGuard />}>
            <Route path="admin" element={suspense(<AdminDashboardPage />)} />
            <Route path="admin/metrics" element={suspense(<FounderMetricsPage />)} />
            <Route path="admin/automated-billing" element={suspense(<AutomatedBillingPage />)} />
            <Route path="admin/error-monitoring" element={suspense(<ErrorMonitoringPage />)} />
            <Route path="admin/security-monitoring" element={suspense(<SecurityMonitoringPage />)} />
          </Route>
          <Route path="settings">
            <Route index element={suspense(<SettingsPage />)} />
            {/* white-label and subscription are org OWNER/ADMIN only */}
            <Route element={<OrgAdminGuard />}>
              <Route path="white-label" element={suspense(<WhiteLabelPage />)} />
              <Route path="subscription" element={suspense(<SubscriptionPage />)} />
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path="error/500" element={suspense(<ServerErrorPage />)} />
      <Route path="*" element={suspense(<NotFoundPage />)} />
    </Route>
  )
);

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Only use hash for in-page anchor scroll if it looks like an element id
    // (OAuth callbacks use #access_token=...&refresh_token=... which is not a valid selector)
    const isAnchorFragment = hash && hash.length > 1 && !hash.includes('=');
    if (isAnchorFragment) {
      try {
        const target = document.querySelector(hash);
        if (target instanceof HTMLElement) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      } catch {
        // Invalid selector, fall through to scroll to top
      }
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname, hash]);

  return null;
}

function RootRoute() {
  return (
    <AppProviders>
      <ScrollToTop />
      <RouteSeoManager />
      <BackendStatusBanner />
      <Outlet />
    </AppProviders>
  );
}

export function AppRouter() {
  return (
    <RouterProvider
      router={router}
      future={{ v7_startTransition: true }}
    />
  );
}
